import { ChangeEventHandler, FC, useEffect, useState } from 'react';
import styles from './styles.module.css';
import { useFormContext } from 'react-hook-form';
import {
  ContractFormData,
  WasteItemsInfo,
} from '../../../../../types/contracts';
import Select, { Option } from '../../../../../components/common/Select';
import FormItem from '../../../../../components/common/FormItem';
import classNames from 'classnames';
import {
  timeOutOfRangeErrorMessage,
  halfWidth,
  invalidTimeErrorMessage,
  max,
  required,
  selected,
  maxValue,
} from '../../../../../message';
import Input from '../../../../../components/common/Input';
import { Company } from '../../../../../types/companies';
import {
  useGetCompaniesWithoutLoadingQuery,
  useGetCompanyQuery,
} from '../../../../../api/companyApi';
import SuggestInput from '../../../../../components/common/SuggestInput';
import {
  correctTimeMinutes,
  validateTimeMinutes,
} from '../../../../../validators/time';
import InputFixedNum from '../../../../../components/common/InputFixedNum';
import { parse } from 'date-fns';
import { ErrorMessage } from '@hookform/error-message';
import Caption, {
  CaptionsUnderForm,
} from '../../../../../components/common/Caption';
import { WasteItem } from '../../../../../types/others';

type Props = {
  index: number;
  wasteItems?: WasteItem[];
};
const labels = {
  wasteItemId: '廃棄品目',
  wasteItemName: '名称',
  amountOfWaste: '廃棄量総排出量',
  disposalSiteCompanyId: '契約先処分場',
  specificTime: '到着予定時間',
  specificTimeStartAt: '指定時間(開始)',
  specificTimeEndAt: '指定時間(終了)',
};
const initCompanies: Company[] = [];
const setValueOpt = {
  shouldValidate: true,
  shouldDirty: true,
  shouldTouch: true,
};
const WasteItemForm: FC<Props> = ({ index, wasteItems }) => {
  const itemKey = `wasteItemsInfo.${index}` as const;
  const { getValues, watch, setValue, getFieldState, register, trigger } =
    useFormContext<ContractFormData>();
  const wasteItemOptions: Option[] = (wasteItems || []).map((item, i) => ({
    label: `${i + 1}.${item.wasteItem}`,
    value: `${item.wasteItemId}`,
  }));
  const wasteItemKey = `${itemKey}.wasteItemId` as const;
  const companyIdKey = `${itemKey}.disposalSiteCompanyId` as const;
  const wasteItemId = watch(wasteItemKey);
  register(wasteItemKey, {
    required: required(labels.wasteItemId),
  });
  const onChangeWasteItemId: ChangeEventHandler<HTMLSelectElement> = (e) => {
    const value = e.target.value;
    setValue(wasteItemKey, Number(value), setValueOpt);
    setValue(companyIdKey, null, setValueOpt);
    setCompanyName('');
  };

  const wasteItemNameKey = `${itemKey}.wasteItemName` as const;
  const amountOfWasteKey = `${itemKey}.amountOfWaste` as const;

  const goesStraight = watch('goesStraight');
  const [companyName, setCompanyName] = useState('');
  const {
    data: { companies } = {
      companies: initCompanies,
    },
  } = useGetCompaniesWithoutLoadingQuery(
    {
      companyName,
      companyTypeId: 3 /** 処分事業者 */,
      companyWasteItemIds: wasteItemId ? [wasteItemId] : undefined,
    },
    { skip: companyName.length > 255 }
  );
  useEffect(() => {
    if (company && !getFieldState(companyIdKey).error) {
      onClickSuggest(company);
    }
    trigger(companyIdKey);
  }, [goesStraight]);

  register(companyIdKey, {
    required: goesStraight && selected(labels.disposalSiteCompanyId),
  });
  const getCompaniesKey = (item: Company) => item.companyId;
  const getCompaniesLabel = (item: Company) => item.companyName;
  const onClickSuggest = (company: Company) => {
    setCompanyName(company.companyName);
    setValue(companyIdKey, company.companyId, setValueOpt);
  };
  const companyId = watch(companyIdKey);
  const { data: company } = useGetCompanyQuery(companyId ?? 0, {
    skip: companyId === null || companyId === undefined,
  });
  useEffect(() => {
    if (company && goesStraight) {
      setCompanyName(company.companyName);
    }
  }, [company?.companyName, goesStraight]);
  const onChangeCompanyName = (companyName: string) => {
    setCompanyName(companyName);
    const hitCompany = companies.find((c) => c.companyName === companyName);
    setValue(companyIdKey, hitCompany?.companyId ?? null, setValueOpt);
  };

  const specificTimeStartAtKey = `${itemKey}.specificTimeStartAt` as const;
  const specificTimeEndAtKey = `${itemKey}.specificTimeEndAt` as const;
  const [specificTimeStartAt, setSpecificTimeStartAt] = useState(
    getValues(specificTimeStartAtKey) || ''
  );
  const [specificTimeEndAt, setSpecificTimeEndAt] = useState(
    getValues(specificTimeEndAtKey) || ''
  );

  const startTimeValidation = () => {
    const { specificTimeStartAt, specificTimeEndAt } =
      getValues('wasteItemsInfo')[index] ?? {};
    return (
      !specificTimeStartAt ||
      !specificTimeEndAt ||
      parse(specificTimeStartAt, 'HH:mm', new Date()) <
        parse(specificTimeEndAt, 'HH:mm', new Date())
    );
  };

  const getVariants = (key: keyof WasteItemsInfo) => {
    if (
      key === 'specificTimeEndAt' &&
      getFieldState(`${itemKey}.specificTimeStartAt`)?.error?.type ===
        'dateValidation'
    )
      return ['error' as const, 'fit' as const];
    return getFieldState(`${itemKey}.${key}`).error
      ? ['error' as const, 'fit' as const]
      : ['fit' as const];
  };

  return (
    <>
      <div className={styles.itemContainer}>
        <div className={styles.itemRow}>
          <FormItem label={labels.wasteItemId}>
            <div className={classNames(styles.item, styles.wasteItemId)}>
              <Select
                options={wasteItemOptions}
                value={wasteItemId === null ? undefined : String(wasteItemId)}
                onChange={onChangeWasteItemId}
                variants={getVariants('wasteItemId')}
              ></Select>
            </div>
          </FormItem>
          <div className={classNames(styles.item, styles.name)}>
            <FormItem label={labels.wasteItemName} optional>
              <div className={classNames(styles.item, styles.name)}>
                <Input
                  {...register(wasteItemNameKey, {
                    validate: {
                      max: (value) =>
                        value.length <= 255 || max(labels.wasteItemName, 255),
                    },
                  })}
                  variants={getVariants('wasteItemName')}
                />
              </div>
            </FormItem>
          </div>
        </div>
        <CaptionsUnderForm>
          {[wasteItemKey, `${itemKey}.wasteItemName`].map((name) => (
            <ErrorMessage
              key={name}
              name={name}
              render={({ message }) => (
                <Caption variants={['error']}>{message}</Caption>
              )}
            />
          ))}
        </CaptionsUnderForm>
      </div>
      <div className={styles.itemContainer}>
        <div className={styles.itemRow}>
          <FormItem label={labels.amountOfWaste}>
            <div className={styles.itemRow}>
              <div className={styles.amountOfWaste}>
                <InputFixedNum
                  name={
                    register(amountOfWasteKey, {
                      required: required(labels.amountOfWaste),
                      validate: {
                        maxValue: (value) =>
                          (value && value <= 1000) ||
                          maxValue(labels.amountOfWaste, 1000),
                      },
                    }).name
                  }
                  variants={getVariants('amountOfWaste')}
                  value={watch(amountOfWasteKey)}
                  digits={2}
                  min={0}
                  max={1000.0}
                  onChange={(x) => setValue(amountOfWasteKey, x, setValueOpt)}
                />
              </div>
              <div className={styles.unit}>m³</div>
            </div>
          </FormItem>
          <FormItem label={labels.disposalSiteCompanyId}>
            <div
              className={classNames(styles.disposalSiteCompanyId, styles.item)}
            >
              <SuggestInput
                list={companies}
                value={goesStraight ? companyName : ''}
                getKey={getCompaniesKey}
                getLabel={getCompaniesLabel}
                onClickSuggest={onClickSuggest}
                onChange={onChangeCompanyName}
                variants={getVariants('disposalSiteCompanyId')}
                disabled={!goesStraight}
              />
            </div>
          </FormItem>
          <FormItem label={labels.specificTime} optional>
            <div className={styles.itemRow}>
              <div className={styles.time}>
                <Input
                  {...register(specificTimeStartAtKey, {
                    validate: {
                      pattern: (v) =>
                        !v ||
                        !v.match(/[０-９ ：]/) ||
                        halfWidth(labels.specificTimeStartAt),
                      time: (v) =>
                        !goesStraight ||
                        !v ||
                        validateTimeMinutes(v) ||
                        invalidTimeErrorMessage,
                      dateValidation: () =>
                        startTimeValidation() || timeOutOfRangeErrorMessage,
                    },
                    onChange: (e) => setSpecificTimeStartAt(e.target.value),
                    onBlur: (e) => {
                      setValue(
                        specificTimeStartAtKey,
                        correctTimeMinutes(e.target.value),
                        { shouldValidate: true }
                      );
                      trigger([specificTimeStartAtKey, specificTimeEndAtKey]);
                    },
                  })}
                  value={goesStraight ? specificTimeStartAt : ''}
                  variants={getVariants('specificTimeStartAt')}
                  disabled={!goesStraight}
                />
              </div>
              <div className={styles.tilda}>~</div>
              <div className={styles.time}>
                <Input
                  {...register(specificTimeEndAtKey, {
                    validate: {
                      pattern: (v) =>
                        !v ||
                        !v.match(/[０-９ ：]/) ||
                        halfWidth(labels.specificTimeEndAt),
                      time: (v) =>
                        !goesStraight ||
                        !v ||
                        validateTimeMinutes(v) ||
                        invalidTimeErrorMessage,
                    },
                    onChange: (e) => setSpecificTimeEndAt(e.target.value),
                    onBlur: (e) => {
                      setValue(
                        specificTimeEndAtKey,
                        correctTimeMinutes(e.target.value),
                        { shouldValidate: true }
                      );
                      trigger([specificTimeStartAtKey, specificTimeEndAtKey]);
                    },
                  })}
                  value={goesStraight ? specificTimeEndAt : ''}
                  variants={getVariants('specificTimeEndAt')}
                  disabled={!goesStraight}
                />
              </div>
            </div>
          </FormItem>
        </div>
        <CaptionsUnderForm>
          {[
            `${itemKey}.amountOfWaste`,
            `${itemKey}.disposalSiteCompanyId`,
            `${itemKey}.specificTimeStartAt`,
            `${itemKey}.specificTimeEndAt`,
          ].map((name) => (
            <ErrorMessage
              key={name}
              name={name}
              render={({ message }) => (
                <Caption variants={['error']}>{message}</Caption>
              )}
            />
          ))}
        </CaptionsUnderForm>
      </div>
    </>
  );
};

export default WasteItemForm;
